import { memo, useCallback, useRef, useState } from "react";
import { GalleryVideoObject } from "../Galleries/utils/utils";
import { formatAmplienceImageURL } from "@JohnLewisPartnership/jl-design-system/dist/utils/imageUrlModifier/imageUrlModifier";
import { IconButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { PlayFilled } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/PlayFilled";
import styles from "./Gallery.module.scss";

type GalleryVideoProps = {
  video: GalleryVideoObject;
  index: number;
  total: number;
  onSelect?: (index: number) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
};

export const GalleryVideo = memo(function GalleryVideo({
  video,
  index,
  total,
  onSelect,
  onKeyDown,
}: GalleryVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlay = useCallback(() => {
    videoRef.current?.play();
    setIsPlaying(true);
  }, []);

  if (onSelect) {
    return (
      <div className={styles.carouselVideo} onKeyDown={onKeyDown}>
        <button
          aria-label={`Play video ${index + 1} of ${total}, ${video.title}`}
          className={styles.carouselImagePlayButton}
          data-testid={`gallery:video-play-button-${index}`}
          onClick={() => onSelect(index)}
        >
          <img
            src={formatAmplienceImageURL(`${video.imageUrl}?wid=720&hei=640`)}
            alt=""
            className={styles.image}
            loading="lazy"
          />
        </button>
        <IconButton
          icon={PlayFilled}
          aria-label={`Play video ${index + 1} of ${total}, ${video.title}`}
          data-testid={`gallery:video-play-${index}`}
          className={styles.carouselPlayButton}
          onClick={() => onSelect(index)}
          background="tonal"
          variant="inverted"
        />
      </div>
    );
  }

  return (
    <div className={styles.videoWrapper}>
      <video
        ref={videoRef}
        className={styles.video}
        controls={isPlaying}
        poster={video.posterUrl}
        src={video.videoUrl}
        aria-label={`Video ${index + 1} of ${total}, ${video.title}`}
        data-testid={`gallery:video-${index}`}
        onKeyDown={onKeyDown}
      />
      {!isPlaying && (
        <>
          <button
            aria-label={`Play video ${index + 1} of ${total}, ${video.title}`}
            data-testid={`gallery:video-play-button-${index}`}
            className={styles.carouselImagePlayButton}
            onClick={handlePlay}
          ></button>
          <IconButton
            icon={PlayFilled}
            aria-label={`Play video ${index + 1} of ${total}, ${video.title}`}
            data-testid={`gallery:video-play-${index}`}
            className={styles.playButton}
            onClick={handlePlay}
            background="tonal"
            variant="inverted"
          />
        </>
      )}
    </div>
  );
});
